import React from "react";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import LiveEventBox from "./LiveEventBox";
import ComingSoonEventBox from "./ComingSoonEventBox";
import CompletedEventBox from "./CompletedEventBox";
import { fetchEvents } from "../../services/firestoreData";
import type { EventRecord } from "../../types/dataTypes";
import { SkeletonCard } from "../common/SkeletonLoader";
import { getCache, setCache, clearExpired } from "../../utils/cacheWithTTL";
import ErrorScreen from "../common/ErrorScreen";
import { getErrorDetails } from "../../utils/errorMapper";

type EventItem = any;

const EVENTS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const parseDate = (dateStr?: string) => {
  if (!dateStr) return new Date(0);
  // Accept either DD-MM-YYYY or YYYY-MM-DD (ISO)
  const parts = dateStr.split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    // ISO: YYYY-MM-DD
    return new Date(`${parts[0]}-${parts[1]}-${parts[2]}`);
  }
  // Assume DMY: DD-MM-YYYY
  const [day, month, year] = parts;
  return new Date(`${year}-${month}-${day}`);
};

// Determine status considering optional start/end times.
const calculateStatus = (
  dateString?: string,
  endDateString?: string,
  startTime?: string,
  endTime?: string,
) => {
  const now = new Date();
  const eventDate = parseDate(dateString);
  if (!eventDate || isNaN(eventDate.getTime())) return "upcoming";

  const endDate = endDateString ? parseDate(endDateString) : eventDate;

  const makeDateTime = (date: Date, time?: string, isEnd = false) => {
    if (!time || !time.trim()) {
      // if no time provided, use start-of-day / end-of-day depending on context
      return isEnd
        ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59)
        : new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    // Normalize time strings like "10:30", "10:30 AM", "22:00"
    const t = time.trim();
    const ampmMatch = t.match(/(am|pm)$/i);
    let hh: number | null = null;
    let mm = 0;

    const timePart = t.replace(/(am|pm)$/i, "").trim();
    const parts = timePart.split(":");
    if (parts.length >= 1) hh = parseInt(parts[0], 10) || 0;
    if (parts.length >= 2) mm = parseInt(parts[1], 10) || 0;

    if (ampmMatch && hh !== null) {
      const ampm = ampmMatch[0].toLowerCase();
      if (ampm === "pm" && hh < 12) hh += 12;
      if (ampm === "am" && hh === 12) hh = 0;
    }

    if (hh === null) hh = 0;
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      hh,
      mm,
      0,
    );
  };

  const startDT = makeDateTime(eventDate, startTime, false);
  const endDT = makeDateTime(endDate, endTime, true);

  if (now >= startDT && now <= endDT) return "live";
  if (now < startDT) return "upcoming";
  return "completed";
};

const formatDateRange = (startStr?: string, endStr?: string) => {
  const start = parseDate(startStr);
  const end = endStr ? parseDate(endStr) : undefined;

  if (!start || isNaN(start.getTime())) return startStr || "";

  const monthName = (d: Date) => d.toLocaleString("en-US", { month: "short" });
  const day = (d: Date) => d.getDate();
  const year = (d: Date) => d.getFullYear();

  if (!end || isNaN(end.getTime())) {
    return `${monthName(start)} ${day(start)}, ${year(start)}`;
  }

  if (year(start) === year(end)) {
    if (start.getMonth() === end.getMonth()) {
      return `${monthName(start)} ${day(start)} – ${day(end)}, ${year(start)}`;
    }
    return `${monthName(start)} ${day(start)} – ${monthName(end)} ${day(
      end
    )}, ${year(start)}`;
  }

  return `${monthName(start)} ${day(start)}, ${year(start)} – ${monthName(
    end
  )} ${day(end)}, ${year(end)}`;
};

const EventsSection: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const navigate = useNavigate();

  const handleNavigateToTimeline = () => {
    navigate("/events#timeline");
  };

  const [events, setEvents] = React.useState<EventRecord[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [revalidating, setRevalidating] = React.useState(false);
  const [error, setError] = React.useState<any | null>(null);

  React.useEffect(() => {
    let mounted = true;

    // Clear expired cache on mount
    clearExpired();

    // Try to get cached events
    const cachedEvents = getCache<EventRecord[]>("events");

    if (cachedEvents) {
      // Show cached data immediately (fast)
      setEvents(cachedEvents);
      setLoading(false);
      setRevalidating(true);
    } else {
      setLoading(true);
    }

    // Fetch fresh data in background (slow)
    fetchEvents({ publishedOnly: true })
      .then((docs) => {
        if (!mounted) return;
        setEvents(docs || []);
        setError(null);
        setRevalidating(false);

        // Update cache
        setCache("events", docs || [], EVENTS_CACHE_TTL);
      })
      .catch((err: any) => {
        if (!mounted) return;
        setError(err);
        setRevalidating(false);

        // If we have no cached data, this is a real error
        if (!cachedEvents) {
          setEvents([]);
        }
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const liveEvents: EventItem[] = [];
  const upcomingEvents: EventItem[] = [];
  const completedEvents: EventItem[] = [];

  const getMostRecentEvent = (eventsArray: EventItem[]) => {
    if (!eventsArray || eventsArray.length === 0) return null;
    return eventsArray.reduce(
      (latest, curr) =>
        parseDate(curr.date || curr.dateISO) >
        parseDate(latest.date || latest.dateISO)
          ? curr
          : latest,
      eventsArray[0],
    );
  };

  for (let event of events) {
    const dateVal = event.date || event.dateISO;
    const status = calculateStatus(dateVal, event.endDate, event.startTime, event.endTime);
    if (status === "live") liveEvents.push(event);
    else if (status === "upcoming") upcomingEvents.push(event);
    else completedEvents.push(event);
  }

  const selectedEvents: EventItem[] = [];

  if (liveEvents.length >= 3) {
    selectedEvents.push(...liveEvents.slice(0, 3));
  } else if (liveEvents.length === 2) {
    selectedEvents.push(...liveEvents);
    if (upcomingEvents.length > 0) selectedEvents.push(upcomingEvents[0]);
  } else if (liveEvents.length === 1) {
    selectedEvents.push(...liveEvents);
    if (upcomingEvents.length >= 1) selectedEvents.push(upcomingEvents[0]);
    const recentCompleted = getMostRecentEvent(completedEvents);
    if (recentCompleted) selectedEvents.push(recentCompleted);
  } else {
    selectedEvents.push(...upcomingEvents.slice(0, 2));
    const recentCompleted = getMostRecentEvent(completedEvents);
    if (recentCompleted) selectedEvents.push(recentCompleted);
  }

  const orderedEvents = [
    ...selectedEvents.filter(
      (e) => calculateStatus(e.date, e.endDate, e.startTime, e.endTime) === "completed",
    ),
    ...selectedEvents.filter(
      (e) => calculateStatus(e.date, e.endDate, e.startTime, e.endTime) === "live",
    ),
    ...selectedEvents.filter(
      (e) => calculateStatus(e.date, e.endDate, e.startTime, e.endTime) === "upcoming",
    ),
  ];

  return (
    <div
      ref={ref}
      style={{ backgroundColor: "black", padding: "3rem", width: "100" }}
    >
      <div className="text-center mb-5">
        <h2
          className="text-white fw-bold"
          style={{ fontSize: "2.5rem", fontFamily: "'Orbitron', sans-serif" }}
        >
          Events Highlight
        </h2>
        <p
          className="text-secondary"
          style={{
            fontSize: "1.1rem",
            fontFamily: "'Share Tech Mono', monospace",
          }}
        >
          The Headliners You Can't Ignore
        </p>
        {revalidating && (
          <span
            className="badge bg-info text-dark"
            style={{
              fontSize: "0.75rem",
              marginTop: "0.5rem",
            }}
          >
            Updating...
          </span>
        )}
      </div>

      {inView && (
        <div className="row g-4 justify-content-center">
          {loading ? (
            <>
              <div className="col-12 col-sm-10 col-md-6 col-lg-4">
                <SkeletonCard show={true} count={1} />
              </div>
              <div className="col-12 col-sm-10 col-md-6 col-lg-4">
                <SkeletonCard show={true} count={1} />
              </div>
              <div className="col-12 col-sm-10 col-md-6 col-lg-4">
                <SkeletonCard show={true} count={1} />
              </div>
            </>
          ) : (
            <>
              {error && !events.length && (
                <div className="col-12">
                  <ErrorScreen {...getErrorDetails(error)} />
                </div>
              )}
              {orderedEvents.map((event, idx) => {
                const status = calculateStatus(
                  event.date,
                  event.endDate,
                  event.startTime,
                  event.endTime,
                );
                const props = {
                  imagePath: event.eventImage,
                  title: event.name,
                  text: event.description,
                  date: formatDateRange(event.date, event.endDate),
                  onClick: handleNavigateToTimeline,
                };

                if (status === "live")
                  return (
                    <div
                      className="col-12 col-sm-10 col-md-6 col-lg-4"
                      key={idx}
                    >
                      <LiveEventBox {...props} />
                    </div>
                  );
                if (status === "upcoming")
                  return (
                    <div
                      className="col-12 col-sm-10 col-md-6 col-lg-4"
                      key={idx}
                    >
                      <ComingSoonEventBox {...props} />
                    </div>
                  );
                return (
                  <div className="col-12 col-sm-10 col-md-6 col-lg-4" key={idx}>
                    <CompletedEventBox {...props} />
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default EventsSection;
